import { HomeyAPI, type SystemMemoryInfo } from 'homey-api';
import { shallow } from 'zustand/shallow';

import { formatBytes } from '../../../lib/formatBytes';

import { BaseApiStore, type FetchArgs } from '../../../store/BaseApiStore.ts';
import { selectBase } from '../../../store/selectors.ts';
import { useAttach } from '../../../store/useAttach';

type MemoryInfoItem = SystemMemoryInfo['types'][string];

interface State {
  memoryInfoData: SystemMemoryInfo | null;
  free: number;
  freeFormatted: string;
  total: number;
  totalFormatted: string;
  swap: number | null;
  swapFormatted: string | null;
  items: (MemoryInfoItem & { key: string; sizeFormatted: string; color: string })[];
  isLoading: boolean;
  error: unknown;
}

type GeneralSettingsInfoMemoryStoreState = State;

function getColorForIndex(index: number) {
  let hue = index * 50;
  hue += 150;
  hue %= 360;

  return `hsl(${hue}, 80%, 70%)`;
}

export class GeneralSettingsInfoMemoryStore extends BaseApiStore<State> {
  constructor() {
    super('general-settings-memory-info');
  }

  override createInitialState() {
    return {
      memoryInfoData: null,
      free: 0,
      freeFormatted: '',
      total: 0,
      totalFormatted: '',
      swap: null,
      swapFormatted: null,
      items: [],
      isLoading: true,
      error: null,
    };
  }

  override async fetch({ api, fetchPolicy, abortController }: FetchArgs) {
    if (fetchPolicy === 'reset') {
      this.set(this.createInitialState());
    }

    if (HomeyAPI.isHomeyAPIV3Cloud(api) === true) {
      this.set({ ...this.createInitialState(), isLoading: false });
      return;
    }

    try {
      // Apply 30s timeout instead of 10s as this can take longer on busy Homeys
      const memoryInfoData = await api.system.getMemoryInfo({
        $timeout: 30000,
      });

      if (abortController.signal.aborted) {
        return;
      }

      const free = memoryInfoData.free ?? 0;
      const total = memoryInfoData.total ?? 0;
      let swap: number | null = memoryInfoData.swap ?? null;

      if (swap === 0) {
        swap = null;
      }

      const items = Object.entries(memoryInfoData.types ?? {})
        .sort(([, left], [, right]) => {
          return (right.size ?? 0) - (left.size ?? 0);
        })
        .map(([key, value], index) => {
          return {
            ...value,
            key,
            sizeFormatted: value.size == null ? '—' : formatBytes(value.size),
            color: getColorForIndex(index),
          };
        });

      this.set({
        ...this.createInitialState(),
        memoryInfoData,
        free,
        freeFormatted: formatBytes(free),
        total,
        totalFormatted: formatBytes(total),
        swap,
        swapFormatted: swap !== null ? formatBytes(swap) : null,
        items,
        isLoading: false,
      });
    } catch (error) {
      if (abortController.signal.aborted) {
        return;
      }

      this.setFetchError({ error, fetchPolicy });
    }
  }

  override destroy() {}

  override onAttached({ api }: { api: HomeyAPI }): void {}

  override onDetached({ api }: { api: HomeyAPI }): void {}

  use() {
    // eslint-disable-next-line react-hooks/rules-of-hooks
    useAttach(this, 'general-settings-info-memory');

    return { generalSettingsInfoMemory: this.store(selectAll, shallow) };
  }
}

function selectAll(state: GeneralSettingsInfoMemoryStoreState) {
  return {
    ...selectBase(state),
    memoryInfoData: state.memoryInfoData,
    free: state.free,
    freeFormatted: state.freeFormatted,
    total: state.total,
    totalFormatted: state.totalFormatted,
    swap: state.swap,
    swapFormatted: state.swapFormatted,
    items: state.items,
  };
}

export const generalSettingsInfoMemoryStore = new GeneralSettingsInfoMemoryStore();
